/** Debug XP grant scene — lets the developer inject arbitrary attunement XP into any skill tree. */
import Phaser from 'phaser';
import { ConfigLoader } from '../../systems/ConfigLoader';
import { crispText, crispButton, setupCrispCamera } from '../../ui/CrispUtil';
import { Font, Hex, TextStyle, ButtonColor, applyButtonHover } from '../../ui/Theme';
import { GAME_WIDTH } from '../../utils/Constants';
import type { GameScene } from '../GameScene';

const XP_PRESETS = [500, 2000, 6000, 99999];
const XP_STEP = 100;

interface XPRow {
  treeName: string;
  xp: number;
  xpText: Phaser.GameObjects.Text;
}

/**
 * Shows current XP per tree alongside +/- steppers and preset buttons (500/2000/6000/MAX).
 * Clicking GRANT calls importProgress() on the live skill progression component, preserving
 * chosen nodes and existing XP.
 */
export class DebugGrantXPScene extends Phaser.Scene {
  private rows: XPRow[] = [];

  constructor() {
    super({ key: 'DebugGrantXPScene' });
  }

  create(): void {
    this.rows = [];
    setupCrispCamera(this);
    const width = GAME_WIDTH;

    this.cameras.main.setBackgroundColor(Hex.sceneBg);

    crispText(this, width / 2, 30, 'GRANT ATTUNEMENT XP', TextStyle.debugTitle).setOrigin(0.5);

    // Current XP display + controls for each tree
    const treeNames = Object.keys(ConfigLoader.v4Trees);
    const startY = 70;
    const lineHeight = 28;
    const labelX = 60;
    const currentX = 170;
    const minusBtnX = 230;
    const xpX = 280;
    const plusBtnX = 330;
    const maxBtnX = 380;

    // Header row
    crispText(this, labelX, startY - 18, 'Tree', TextStyle.smallMuted).setOrigin(0, 0.5);
    crispText(this, currentX, startY - 18, 'Current', TextStyle.smallMuted).setOrigin(0.5, 0.5);
    crispText(this, xpX, startY - 18, 'Grant', TextStyle.smallMuted).setOrigin(0.5, 0.5);

    const gs = this.scene.get('GameScene') as GameScene;
    const prog = gs?.player?.skillProgression;

    for (let i = 0; i < treeNames.length; i++) {
      const treeName = treeNames[i];
      const y = startY + i * lineHeight;
      const currentXP = prog?.getTreeXp(treeName) ?? 0;

      // Tree name
      const displayName = treeName.charAt(0).toUpperCase() + treeName.slice(1);
      crispText(this, labelX, y, displayName, TextStyle.body).setOrigin(0, 0.5);

      // Current XP
      crispText(this, currentX, y, String(Math.round(currentXP)), {
        fontFamily: Font.family, fontSize: Font.sizeBody, color: Hex.textSecondary,
      }).setOrigin(0.5, 0.5);

      // Minus button
      const minusBtn = crispText(this, minusBtnX, y, '-', {
        ...TextStyle.body, color: Hex.debugDefault,
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });

      // XP amount to grant
      const xpText = crispText(this, xpX, y, '0', {
        fontFamily: Font.family, fontSize: Font.sizeBody, color: '#ffffff',
      }).setOrigin(0.5);

      // Plus button
      const plusBtn = crispText(this, plusBtnX, y, '+', {
        ...TextStyle.body, color: Hex.debugDefault,
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });

      // Max button
      const maxBtn = crispText(this, maxBtnX, y, 'MAX', {
        fontFamily: Font.family, fontSize: Font.sizeSmall, color: Hex.orangeDefault,
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });

      const row: XPRow = { treeName, xp: 0, xpText };
      this.rows.push(row);

      minusBtn.on('pointerdown', () => {
        row.xp = Math.max(0, row.xp - XP_STEP);
        row.xpText.setText(String(row.xp));
      });

      plusBtn.on('pointerdown', () => {
        row.xp += XP_STEP;
        row.xpText.setText(String(row.xp));
      });

      maxBtn.on('pointerdown', () => {
        row.xp = 99999;
        row.xpText.setText(String(row.xp));
      });

      applyButtonHover(minusBtn, ButtonColor.debug);
      applyButtonHover(plusBtn, ButtonColor.debug);
      applyButtonHover(maxBtn, ButtonColor.warning);
    }

    // Preset buttons: quick-set all trees to a threshold
    const presetY = startY + treeNames.length * lineHeight + 20;
    crispText(this, width / 2, presetY, 'SET ALL TO:', TextStyle.smallMuted).setOrigin(0.5);

    const presetBtnY = presetY + 20;
    const presetSpacing = 80;
    const presetStartX = width / 2 - (presetSpacing * (XP_PRESETS.length - 1)) / 2;

    for (let i = 0; i < XP_PRESETS.length; i++) {
      const val = XP_PRESETS[i];
      const label = val === 99999 ? 'MAX' : String(val);
      crispButton(this, presetStartX + i * presetSpacing, presetBtnY,
        `${label}`, Font.sizeButtonXs, ButtonColor.debug, () => {
          for (const row of this.rows) {
            row.xp = val;
            row.xpText.setText(String(val));
          }
        });
    }

    // Action buttons (V4 has fixed 4 hotbar slots — no slot growth)
    const btnY = presetBtnY + 40;

    crispButton(this, width / 2, btnY, 'GRANT',
      Font.sizeSmall, ButtonColor.primary, () => this.doGrant());

    this.input.keyboard!.on('keydown-ESC', () => this.scene.start('DebugSettingsScene'));
  }

  private doGrant(): void {
    const gs = this.scene.get('GameScene') as GameScene;
    if (!gs?.player) return;

    const prog = gs.player.skillProgression;
    for (const row of this.rows) {
      if (row.xp <= 0) continue;
      prog.grantXp(row.treeName, row.xp);
    }

    // Return to debug settings
    this.scene.start('DebugSettingsScene');
  }
}
